import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
} from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import LogoutIcon from "@mui/icons-material/Logout";
import LanIcon from "@mui/icons-material/Lan";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import { alpha } from "@mui/material/styles";
import ConnectionSettings from "../ConnectionSettings";
import AuditPanel from "../AuditPanel";
import { useAuth } from "../../contexts/AuthContext";
import { DarkModeContext } from "../../App";

const ROLE_LABELS = {
  admin: "Admin",
  fdc: "FDC",
  gun_commander: "Gun Cmdr",
};

export default function TopModeBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const mode = location.pathname.startsWith("/guncmd") ? "guncmd" : "fdc";
  const appBarRef = useRef(null);

  const { user, logout, hasRole } = useAuth();
  const { darkMode, setDarkMode } = useContext(DarkModeContext);
  const [netOpen, setNetOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  const canAccessFdc = hasRole("admin", "fdc");
  const canAccessGuns = hasRole("admin", "gun_commander");

  useEffect(() => {
    const el = appBarRef.current;
    if (!el) return;

    const updateHeight = () => {
      const height = Math.ceil(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty(
        "--topmodebar-height",
        `${height}px`,
      );
    };

    updateHeight();

    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);
    window.addEventListener("resize", updateHeight);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const displayName = user?.display_name || user?.username || "";
  const roleLabel = ROLE_LABELS[user?.role] || user?.role || "";

  return (
    <AppBar
      ref={appBarRef}
      position="sticky"
      elevation={0}
      sx={(theme) => ({
        zIndex: theme.zIndex.appBar,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        color: "text.primary",
        overflow: "hidden",
      })}
    >
      <Toolbar
        sx={{
          gap: { xs: 0.5, sm: 1 },
          justifyContent: "space-between",
          minHeight: { xs: 48, sm: 56 },
          px: { xs: 1, sm: 2 },
        }}
      >
        {/* Left: branding — compact on mobile */}
        <Box sx={{ flexShrink: 0 }}>
          <Typography
            sx={{ fontWeight: 700, lineHeight: 1, letterSpacing: 0.3, fontSize: { xs: 15, sm: 18 } }}
          >
            CHACAL
          </Typography>
          <Typography
            variant="caption"
            sx={{ opacity: 0.75, display: { xs: "none", sm: "block" } }}
          >
            Fire Mission Calculator
          </Typography>
        </Box>

        {/* Right: controls — overflow safe */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1 },
            minWidth: 0,
            flexShrink: 1,
          }}
        >
          {/* Page toggle — only if user can see both */}
          {canAccessFdc && canAccessGuns && (
            <ToggleButtonGroup
              exclusive
              value={mode}
              onChange={(_e, v) => {
                if (!v) return;
                navigate(v === "guncmd" ? "/guncmd" : "/");
              }}
              size="small"
              sx={{
                flexShrink: 0,
                bgcolor: "background.paper",
                borderRadius: 999,
                p: 0.35,
                "& .MuiToggleButton-root": {
                  border: 0,
                  borderRadius: 999,
                  px: { xs: 1.25, sm: 2 },
                  py: 0.35,
                  fontWeight: 600,
                  fontSize: { xs: 12, sm: 14 },
                  textTransform: "none",
                },
              }}
            >
              <ToggleButton value="fdc">FDC</ToggleButton>
              <ToggleButton value="guncmd">Guns</ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* User name — truncated, hidden on very small screens */}
          {user && (
            <Tooltip title={`${displayName} (${roleLabel})`}>
              <Typography
                noWrap
                sx={(theme) => ({
                  fontSize: 11,
                  fontWeight: 700,
                  maxWidth: { xs: 80, sm: 160 },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "text.secondary",
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 999,
                  bgcolor: alpha(
                    theme.palette.text.primary,
                    theme.palette.mode === "dark" ? 0.06 : 0.04,
                  ),
                  border: "1px solid",
                  borderColor: alpha(theme.palette.divider, 0.5),
                  flexShrink: 1,
                  minWidth: 0,
                })}
              >
                {displayName}
              </Typography>
            </Tooltip>
          )}

          {canAccessFdc && (
            <Tooltip title="Audit — solver math">
              <IconButton
                size="small"
                onClick={() => setAuditOpen(true)}
                color="inherit"
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "action.hover",
                  width: 32,
                  height: 32,
                }}
              >
                <CalculateRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Connection / Network">
            <IconButton
              size="small"
              onClick={() => setNetOpen(true)}
              color="inherit"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "action.hover",
                width: 32,
                height: 32,
              }}
            >
              <LanIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <IconButton
            size="small"
            onClick={() => setDarkMode((prev) => !prev)}
            color="inherit"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "action.hover",
              width: 32,
              height: 32,
            }}
          >
            {darkMode ? <LightModeIcon sx={{ fontSize: 18 }} /> : <DarkModeIcon sx={{ fontSize: 18 }} />}
          </IconButton>

          <Tooltip title="Logout">
            <IconButton
              size="small"
              onClick={handleLogout}
              color="inherit"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "action.hover",
                width: 32,
                height: 32,
              }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
      <ConnectionSettings open={netOpen} onClose={() => setNetOpen(false)} />
      <AuditPanel open={auditOpen} onClose={() => setAuditOpen(false)} />
    </AppBar>
  );
}
